import { DeliveryType, ShippingEstimate, ShippingOptionDelivery } from '@pedix-workspace/utils';
import { ShippingCostRequest } from './delivery.types';
import {
  createMinimumPurchaseErrorResponse,
  createFreeShippingResponse,
  createToBeDefinedResponse,
  createFixedCostResponse,
  createOutOfDeliveryZoneResponse,
  createShippingEstimateResponse,
  createDeliveryCostResponse,
} from './shipping-cost-response-creators';
import { fetchShippingEstimates } from './fetch-delivery-estimates';
import { getDeliveryCostByZone } from './delivery-zones.utils';
import { ShippingCostResponse } from './shipping-cost-response.types';

export async function calculateDeliveryCost(
  shippingOptionDelivery: ShippingOptionDelivery,
  request: ShippingCostRequest,
): Promise<ShippingCostResponse | null> {
  const { totalAmountWithDiscount, address, hasLogisticsIntegration } = request;

  if (shippingOptionDelivery.deliveryOperation === 'OWN') {
    return calculateOwnDeliveryCost(shippingOptionDelivery, totalAmountWithDiscount, address);
  }

  if (shippingOptionDelivery.deliveryOperation === 'INTEGRATION') {
    return await calculateIntegrationDeliveryCost(
      shippingOptionDelivery,
      request,
      hasLogisticsIntegration,
    );
  }

  return null;
}

function calculateOwnDeliveryCost(
  shippingOption: ShippingOptionDelivery,
  totalAmount: number,
  address?: ShippingCostRequest['address'],
): ShippingCostResponse | null {
  const { deliveryType, minimumPurchase, minimumPurchaseForFreeShipping, deliveryCost } =
    shippingOption;

  if (deliveryType === DeliveryType.TO_BE_DEFINED || deliveryType === DeliveryType.FIXED) {
    if (isMinimumPurchaseNotMet(totalAmount, minimumPurchase)) {
      return createMinimumPurchaseErrorResponse(minimumPurchase, shippingOption);
    }
    if (isFreeShippingGranted(totalAmount, minimumPurchaseForFreeShipping)) {
      return createFreeShippingResponse(minimumPurchaseForFreeShipping, shippingOption);
    }
  }

  switch (deliveryType) {
    case DeliveryType.TO_BE_DEFINED:
      return createToBeDefinedResponse(shippingOption);
    case DeliveryType.FIXED:
      return createFixedCostResponse(deliveryCost, shippingOption);
    case DeliveryType.DELIVERY_ZONES:
      return calculateZoneBasedDeliveryCost(address, shippingOption, totalAmount);
    default:
      return null;
  }
}

async function calculateIntegrationDeliveryCost(
  shippingOption: ShippingOptionDelivery,
  request: ShippingCostRequest,
  hasLogisticsIntegration: boolean,
): Promise<ShippingCostResponse | null> {
  const { totalAmountWithDiscount, address } = request;
  const { minimumPurchase, minimumPurchaseForFreeShipping } = shippingOption;

  if (!address?.addressDetails) {
    // Not enough information to calculate shipping cost
    return null;
  }

  if (!hasLogisticsIntegration) {
    return createOutOfDeliveryZoneResponse(shippingOption);
  }

  if (isMinimumPurchaseNotMet(totalAmountWithDiscount, minimumPurchase)) {
    return createMinimumPurchaseErrorResponse(minimumPurchase, shippingOption);
  }

  // Fetch estimates first
  const shippingEstimate: ShippingEstimate = request.previousShippingEstimate
    ? request.previousShippingEstimate
    : await fetchShippingEstimates(request.apiBaseUrl, request.establishment, {
        cartItems: request.cartItems,
        personalInformation: {
          name: request.name,
          phone: request.phone,
          address: request.address,
        },
      }).then(shippingEstimates => shippingEstimates?.[0]);

  if (!shippingEstimate) {
    return createOutOfDeliveryZoneResponse(shippingOption);
  }

  const isFree = isFreeShippingGranted(totalAmountWithDiscount, minimumPurchaseForFreeShipping);

  return createShippingEstimateResponse(
    shippingEstimate,
    shippingOption,
    isFree,
    minimumPurchaseForFreeShipping,
  );
}

function calculateZoneBasedDeliveryCost(
  address: ShippingCostRequest['address'] | undefined,
  shippingOption: ShippingOptionDelivery,
  totalAmount: number,
): ShippingCostResponse | null {
  if (!address?.addressDetails) {
    return null;
  }

  const matchingZone = getDeliveryCostByZone(
    address.addressDetails.coordinates,
    shippingOption.deliveryZones,
  );

  if (!matchingZone) {
    return createOutOfDeliveryZoneResponse(shippingOption);
  }

  if (isMinimumPurchaseNotMet(totalAmount, matchingZone.minimumPurchase)) {
    return createMinimumPurchaseErrorResponse(matchingZone.minimumPurchase, shippingOption);
  }

  if (isFreeShippingGranted(totalAmount, matchingZone.minimumPurchaseForFreeShipping)) {
    return createFreeShippingResponse(matchingZone.minimumPurchaseForFreeShipping, shippingOption);
  }

  return createDeliveryCostResponse(
    matchingZone.cost,
    matchingZone.minimumPurchaseForFreeShipping,
    shippingOption,
  );
}

const isMinimumPurchaseNotMet = (total: number, minimum?: number | null) =>
  minimum ? total < minimum : false;
const isFreeShippingGranted = (total: number, minimum?: number | null) =>
  minimum ? total >= minimum : false;
